import {
  type MetaFunction,
  Outlet,
  useLoaderData,
  useMatches,
  useRouteLoaderData,
} from 'react-router';
import { z } from 'zod';

import { GeneralErrorBoundary } from '~/components/error-boundary';
import { MainPageLayout } from '~/components/layout/main-page-layout';
import { getUserLayoutInfo } from '~/domain/users/queries/get-user-layout-info.server';
import { getSessionId, requireSession } from '~/utils/auth.server';
import { handleDomainError } from '~/utils/errors.server.ts';

import { type Route } from './+types/_layout';

export const meta: MetaFunction = () => {
  return [
    { title: 'Fighters Passport | Find Events' },
    {
      name: 'description',
      content: 'Find events.',
    },
  ];
};

export async function loader({ request }: Route.LoaderArgs) {
  // Pass user data to the layout if logged in, otherwise pass null
  if (!(await getSessionId(request))) {
    return null;
  }

  const { userId } = await requireSession(request);
  const result = await getUserLayoutInfo(userId);

  return result.mapErr(handleDomainError).unwrap();
}

const handleSchema = z.object({
  title: z.string().optional().nullable(),
  layout: z.enum(['public', 'private']).optional().nullable(),
});

const useHandle = () => {
  const matches = useMatches();

  const handles = matches
    .filter((m) => m.handle !== undefined)
    .map((m) => handleSchema.safeParse(m.handle))
    .filter((m) => m.success)
    .map((m) => m.data);

  return handles.length > 0 ? handles[0] : undefined;
};

export default function EventsLayout() {
  const data = useLoaderData<typeof loader>();
  return (
    <Layout data={data}>
      <Outlet />
    </Layout>
  );
}

type LayoutProps = React.PropsWithChildren<{
  data: Awaited<ReturnType<typeof loader>>;
}>;

function Layout({ data, children }: LayoutProps) {
  const handle = useHandle();
  const layout = handle?.layout ?? 'public';

  return (
    <MainPageLayout userLayoutData={data} showFooter={layout === 'public'}>
      <main className="size-full">{children}</main>
    </MainPageLayout>
  );
}

export function ErrorBoundary() {
  const data = useRouteLoaderData<typeof loader>('routes/events+/_layout');
  return data ? (
    <Layout data={data}>
      <GeneralErrorBoundary />
    </Layout>
  ) : (
    <GeneralErrorBoundary />
  );
}
